export function formatDistanceToNow(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears}y ago`;
}

export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return num.toString();
}

export const NOOP = () => {};

export const DEFAULT_TITLE = 'MotoWalla - Your Automotive Homepage';

export function slugify(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function slugToTitle(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function setMetaDescription(content: string): void {
  upsertMeta('name', 'description', content);
}

export function upsertMeta(attr: string, key: string, content: string): HTMLMetaElement {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

// SEO-913: Auto-generate branded OG image URL via /og-image/page when no explicit image is provided.
const OG_IMAGE_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function buildAutoOgImage(title: string, category?: string): string {
  const params = new URLSearchParams({ title, category: category || 'auto' });
  return `${OG_IMAGE_BASE}/og-image/page?${params}`;
}

export function setOgMeta(opts: { title: string; description: string; url: string; image?: string; category?: string }): () => void {
  // prevContent: undefined = tag was created (remove on cleanup), null = tag had no content attr, string = restore
  type TagRecord = { el: HTMLMetaElement; prevContent: string | null | undefined };
  const records: TagRecord[] = [];

  function upsert(attr: string, key: string, content: string) {
    const existing = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
    const prevContent = existing ? existing.getAttribute('content') : undefined;
    const el = upsertMeta(attr, key, content);
    records.push({ el, prevContent });
  }

  // SEO-913: fall back to dynamically generated branded OG image when no explicit image
  const image = opts.image || buildAutoOgImage(opts.title, opts.category);

  upsert('property', 'og:type', 'website');
  upsert('property', 'og:title', opts.title);
  upsert('property', 'og:description', opts.description);
  upsert('property', 'og:url', opts.url);
  upsert('property', 'og:site_name', 'MotoWalla');
  upsert('property', 'og:image', image);
  upsert('property', 'og:image:width', '1200');
  upsert('property', 'og:image:height', '630');
  upsert('property', 'og:image:alt', opts.title);
  upsert('property', 'og:locale', 'en_IN');
  upsert('property', 'og:locale:alternate', 'hi_IN');
  upsert('name', 'twitter:card', 'summary_large_image');
  upsert('name', 'twitter:title', opts.title);
  upsert('name', 'twitter:description', opts.description);
  upsert('name', 'twitter:image', image);
  upsert('name', 'twitter:image:alt', opts.title);

  return () => records.forEach(({ el, prevContent }) => {
    if (prevContent === undefined) {
      el.remove();
    } else if (prevContent !== null) {
      el.setAttribute('content', prevContent);
    } else {
      el.removeAttribute('content');
    }
  });
}

const MOTOWALLA_PUBLISHER = {
  '@type': 'Organization',
  name: 'MotoWalla',
  url: 'https://motowalla.com',
  logo: {
    '@type': 'ImageObject',
    url: 'https://motowalla.com/logo.png',
  },
};

function enrichArticleSchema(schema: Record<string, unknown>): Record<string, unknown> {
  if (schema['@type'] !== 'Article' && schema['@type'] !== 'NewsArticle') return schema;
  const enriched = { ...schema };
  if (!enriched.publisher) enriched.publisher = MOTOWALLA_PUBLISHER;
  if (!enriched.mainEntityOfPage && typeof window !== 'undefined') {
    enriched.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': window.location.href,
    };
  }
  if (!enriched.inLanguage) enriched.inLanguage = 'en-IN';
  if (enriched.isAccessibleForFree === undefined) enriched.isAccessibleForFree = true;
  // SEO-415: speakable selectors so Google Assistant / Nest / Alexa can read article aloud.
  if (!enriched.speakable) {
    enriched.speakable = {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '[data-speakable]', 'meta[name="description"]'],
    };
  }
  return enriched;
}

export function buildArticleSchema(opts: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    author: { '@type': 'Organization', name: 'MotoWalla' },
    publisher: MOTOWALLA_PUBLISHER,
    datePublished: opts.datePublished,
    ...(opts.dateModified && { dateModified: opts.dateModified }),
    inLanguage: 'en-IN',
  };
}

/** SEO-416: Author entity links keyed by lowercased name. Adding Wikipedia +
 *  Wikidata URLs to a Person's sameAs lifts E-E-A-T by tying the author to a
 *  recognized knowledge-graph identity. Keys are lowercased; lookup is name-only
 *  to keep the registry small and reviewable. */
const AUTHOR_ENTITY_LINKS: Record<string, ReadonlyArray<string>> = {
  'hormazd sorabjee': [
    'https://en.wikipedia.org/wiki/Hormazd_Sorabjee',
    'https://www.wikidata.org/wiki/Q5895812',
  ],
  'sirish chandran': [
    'https://en.wikipedia.org/wiki/Sirish_Chandran',
  ],
};

/** Build schema.org author — Person when individual name, Organization otherwise */
export function buildAuthorSchema(
  authorName: string | null | undefined,
  source: { name: string; url: string; logoUrl?: string | null } | null | undefined,
): Record<string, unknown> {
  // Individual author → Person with sameAs linking to source
  if (authorName && authorName !== source?.name) {
    const knownLinks = AUTHOR_ENTITY_LINKS[authorName.trim().toLowerCase()];
    const sameAsList = [
      ...(knownLinks ?? []),
      ...(source?.url ? [source.url] : []),
    ];
    return {
      '@type': 'Person',
      name: authorName,
      ...(sameAsList.length === 1 && { sameAs: sameAsList[0] }),
      ...(sameAsList.length > 1 && { sameAs: sameAsList }),
    };
  }
  // Source org or MotoWalla fallback → Organization
  const orgName = source?.name || 'MotoWalla';
  return {
    '@type': 'Organization',
    name: orgName,
    ...(source?.url && { url: source.url }),
    ...(source?.logoUrl && {
      logo: { '@type': 'ImageObject', url: source.logoUrl },
    }),
  };
}

/** Map post category to schema.org articleSection label */
export function categoryToArticleSection(
  category: string,
): string {
  const map: Record<string, string> = {
    cars: 'Cars',
    motorcycles: 'Motorcycles',
    scooters: 'Scooters',
    ev: 'Electric Vehicles',
    adv: 'Adventure',
  };
  return map[category] || 'Automotive';
}

/**
 * SEO-387: Wrap an array of Vehicle (or other) schema entities in a schema.org
 * ItemList so search engines treat the page as a structured comparison.
 * Returns a single ItemList with each input embedded as a ListItem.item.
 */
export function buildVehicleComparisonSchema(
  comparisonName: string,
  vehicles: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: comparisonName,
    numberOfItems: vehicles.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: vehicles.map((v, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: v,
    })),
  };
}

export function injectJsonLd(id: string, schema: unknown): void {
  document.getElementById(id)?.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  const enriched = Array.isArray(schema)
    ? schema.map((s) => (s && typeof s === 'object' && '@type' in s ? enrichArticleSchema(s as Record<string, unknown>) : s))
    : schema && typeof schema === 'object' && '@type' in (schema as Record<string, unknown>)
      ? enrichArticleSchema(schema as Record<string, unknown>)
      : schema;
  script.textContent = JSON.stringify(enriched);
  script.id = id;
  document.head.appendChild(script);
}

// SEO-106: Brand/section labels that slugToTitle() gets wrong (acronyms, multi-word brands)
const BREADCRUMB_LABEL_OVERRIDES: Record<string, string> = {
  bikes: 'Bikes',
  cars: 'Cars',
  guides: 'Guides',
  data: 'Data',
  ev: 'EV',
  scooters: 'Scooters',
  bmw: 'BMW',
  ktm: 'KTM',
  mg: 'MG',
  tvs: 'TVS',
  bsa: 'BSA',
  vinfast: 'VinFast',
  'royal-enfield': 'Royal Enfield',
  'mercedes-benz': 'Mercedes-Benz',
  'maruti-suzuki': 'Maruti Suzuki',
  mercedes: 'Mercedes',
  volkswagen: 'Volkswagen',
  hyundai: 'Hyundai',
  toyota: 'Toyota',
  tata: 'Tata',
  mahindra: 'Mahindra',
  honda: 'Honda',
  hero: 'Hero',
  yamaha: 'Yamaha',
  suzuki: 'Suzuki',
  kia: 'Kia',
  skoda: 'Skoda',
  renault: 'Renault',
  nissan: 'Nissan',
  triumph: 'Triumph',
  bajaj: 'Bajaj',
  ather: 'Ather',
};

function breadcrumbLabel(segment: string): string {
  return BREADCRUMB_LABEL_OVERRIDES[segment] ?? slugToTitle(segment);
}

/**
 * SEO-106: Build a schema.org BreadcrumbList for a pathname.
 * Returns null for root `/` (no breadcrumb needed).
 * Segments map to labels via an override table for brand acronyms; falls back to slugToTitle.
 */
export function buildBreadcrumbSchema(
  pathname: string,
  origin: string,
): Record<string, unknown> | null {
  const clean = pathname.replace(/\/+$/, '');
  if (!clean || clean === '/') return null;

  const segments = clean.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const items: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
  ];

  let accumulated = '';
  segments.forEach((seg, i) => {
    accumulated += `/${seg}`;
    items.push({
      '@type': 'ListItem',
      position: i + 2,
      name: breadcrumbLabel(seg),
      item: `${origin}${accumulated}`,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

// SEO-410: Parse Indian Lakh price strings to numeric INR values.
// Handles: "₹24.99 Lakh", "Rs 1.93 Lakh", "~Rs 2.30 Lakh", "₹17.99–23.50 Lakh (ex-showroom)"
export function parseLakhPrice(priceStr: string): number | null {
  const cleaned = priceStr.replace(/[₹~,]/g, '').replace(/^Rs\s*/i, '').trim();
  const match = cleaned.match(/([\d.]+)/);
  if (!match) return null;
  return Math.round(parseFloat(match[1]) * 100000);
}

// SEO-410: Build schema.org Vehicle schemas with numeric PriceSpecification.
// Generates one Vehicle entity per variant with proper Offer + UnitPriceSpecification,
// enabling Google rich results for vehicle pricing.
export function buildVehiclePriceSchemas(
  opts: {
    brand: string;
    variants: ReadonlyArray<{ name: string; price: string }>;
    fuelType?: string;
    engineName?: string;
  },
): Array<Record<string, unknown>> {
  return opts.variants
    .map((v) => {
      const numericPrice = parseLakhPrice(v.price);
      if (numericPrice === null) return null;
      return {
        '@context': 'https://schema.org',
        '@type': 'Vehicle',
        name: `${opts.brand} ${v.name}`,
        brand: { '@type': 'Brand', name: opts.brand },
        model: v.name,
        ...(opts.fuelType && { fuelType: opts.fuelType }),
        ...(opts.engineName && {
          vehicleEngine: { '@type': 'EngineSpecification', name: opts.engineName },
        }),
        offers: {
          '@type': 'Offer',
          priceCurrency: 'INR',
          price: numericPrice,
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: numericPrice,
            priceCurrency: 'INR',
            unitText: 'ex-showroom',
          },
          availability: 'https://schema.org/InStock',
        },
      };
    })
    .filter((s) => s !== null) as Array<Record<string, unknown>>;
}

// SEO-409: Build a schema.org FAQPage JSON-LD object from Q&A pairs.
// Used on vehicle buyer guide pages to enable Google FAQ rich results.
export function buildFaqSchema(
  faqs: Array<{ question: string; answer: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// SEO-411: Build a schema.org Event JSON-LD object for upcoming vehicle launches.
// Eligible for Google rich results (event date in SERP) when used on guide pages
// for models with confirmed launch dates (e.g., Tata Sierra EV → 2026-06-30).
export function buildEventSchema(opts: {
  name: string;
  description: string;
  startDate: string; // ISO 8601 (e.g., '2026-05-19')
  endDate?: string;
  url: string;
  location?: string; // City/region of launch event; defaults to India
  organizerName?: string; // Manufacturer; defaults to organizer-less event
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: opts.name,
    description: opts.description,
    startDate: opts.startDate,
    endDate: opts.endDate ?? opts.startDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: opts.location ?? 'India',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'IN',
        ...(opts.location && { addressLocality: opts.location }),
      },
    },
    url: opts.url,
    ...(opts.organizerName && {
      organizer: {
        '@type': 'Organization',
        name: opts.organizerName,
      },
    }),
  };
}

// ANALYTICS-060: Auto-generate FAQ rich snippets from vehicle SPECS/VARIANTS data.
// Produces a FAQPage JSON-LD schema that Google can surface in search results.
// Spec labels are matched case-insensitively; unrecognised labels are skipped.
export function buildVehicleFaqSchema(
  vehicleName: string,
  specs: ReadonlyArray<{ label: string; value: string }>,
  variants?: ReadonlyArray<{ name: string; price: string }>,
): Record<string, unknown> {
  const find = (pattern: RegExp) => specs.find((s) => pattern.test(s.label));
  const faqs: Array<{ question: string; answer: string }> = [];

  // Price
  const price = find(/^price/i);
  if (price) {
    faqs.push({
      question: `What is the price of ${vehicleName} in India?`,
      answer: `The ${vehicleName} is priced at ${price.value}.`,
    });
  } else if (variants?.length) {
    const prices = variants.map((v) => v.price);
    faqs.push({
      question: `What is the price of ${vehicleName} in India?`,
      answer: `The ${vehicleName} ranges from ${prices[0]} to ${prices[prices.length - 1]} (ex-showroom).`,
    });
  }

  // Engine / Motor
  const engine = find(/^(engine|motor)$/i);
  if (engine) {
    const label = /motor/i.test(engine.label) ? 'motor' : 'engine';
    faqs.push({
      question: `What ${label} does the ${vehicleName} have?`,
      answer: `The ${vehicleName} is powered by a ${engine.value}.`,
    });
  }

  // Range / Mileage
  const range = find(/^(range|mileage)$/i);
  if (range) {
    faqs.push({
      question: `What is the ${range.label.toLowerCase()} of the ${vehicleName}?`,
      answer: `The ${vehicleName} offers ${range.value}.`,
    });
  }

  // Variants
  if (variants && variants.length > 1) {
    const names = variants.map((v) => v.name);
    faqs.push({
      question: `How many variants does the ${vehicleName} have?`,
      answer: `The ${vehicleName} is available in ${variants.length} variants: ${names.join(', ')}.`,
    });
  }

  // Launch
  const launch = find(/^(launch|bookings)$/i);
  if (launch) {
    faqs.push({
      question: `When is the ${vehicleName} launching in India?`,
      answer: launch.value,
    });
  }

  // Rivals
  const rivals = find(/^rivals?$/i);
  if (rivals) {
    faqs.push({
      question: `What are the rivals of the ${vehicleName}?`,
      answer: `The ${vehicleName} competes with ${rivals.value}.`,
    });
  }

  return buildFaqSchema(faqs);
}

// SEO-982: Auto-generate FAQPage JSON-LD for article/post pages.
// Derives 2-4 FAQ Q&A pairs from AI-extracted entities and metadata so
// article pages are eligible for Google "People Also Ask" rich results.
export function buildArticleFaqSchema(post: {
  title: string;
  category: string;
  aiSummaryShort?: string | null;
  excerpt?: string | null;
  aiEntities?: {
    brands?: string[];
    vehicles?: string[];
    vehicle_types?: string[];
    price_range?: string | null;
    topics?: string[];
  } | null;
}): Record<string, unknown> | null {
  const faqs: Array<{ question: string; answer: string }> = [];
  const summary = post.aiSummaryShort || post.excerpt;

  // Q1: What is this article about? → AI summary as answer
  if (summary) {
    faqs.push({
      question: `What does this article cover?`,
      answer: summary,
    });
  }

  const entities = post.aiEntities;
  if (entities) {
    // Q2: Price range if available
    if (entities.price_range) {
      const vehicleName = entities.vehicles?.[0] || 'this vehicle';
      faqs.push({
        question: `What is the expected price of ${vehicleName} in India?`,
        answer: `The expected price range is ${entities.price_range}.`,
      });
    }

    // Q3: Vehicles discussed
    if (entities.vehicles && entities.vehicles.length > 0) {
      const section = categoryToArticleSection(post.category);
      faqs.push({
        question: `Which ${section.toLowerCase()} are discussed in this article?`,
        answer: `This article covers ${entities.vehicles.join(', ')}.`,
      });
    }

    // Q4: Key topics
    if (entities.topics && entities.topics.length >= 2) {
      faqs.push({
        question: `What are the key topics covered?`,
        answer: `Key topics include ${entities.topics.slice(0, 5).join(', ')}.`,
      });
    }
  }

  // Need at least 2 Q&A pairs for meaningful FAQ schema
  if (faqs.length < 2) return null;

  return buildFaqSchema(faqs);
}
